/**
 * Ecosystem route configuration map.
 *
 * Every subroute in DONiTSH!T's IA (Wellness, Relationships, Families,
 * Business & Corporate) is rendered by EcosystemRoute.jsx using one of
 * the entries below. Centralizes content for fast iteration and
 * consistent UX, while still allowing each topic to feel editorial.
 *
 * Add a new topic = add an entry here. The route is generated automatically
 * by `App.js` from the keys (e.g. `wellness/sleep` -> `/wellness/sleep`).
 */
import { DEEP_CONTENT } from "./ecosystemDeepContent";

const BASE_TOPICS = {
  // ============================================================
  // WELLNESS
  // ============================================================
  "wellness/mental": {
    eyebrow: "— Wellness · Mental Wellness",
    title: "Mental Wellness",
    subtitle:
      "Stress, anxiety, burnout, confidence, motivation, and emotional resilience — practical, science-informed tools to build a quieter, stronger mind.",
    image:
      "https://images.unsplash.com/photo-1490730141103-6cac27aaab94?auto=format&fit=crop&w=1600&q=80",
    marketplaceTopic: "mental-health",
    marketplaceTitle: "Find a mental-wellness practitioner",
    askDonnyPrompt: "Help me build a daily mental-wellness routine that fits a busy life.",
    pillars: [
      { title: "Stress & Anxiety", body: "How chronic stress reshapes attention, sleep, and decision-making — and the small habits that bend it back." },
      { title: "Burnout Recovery", body: "Why rest alone doesn't fix burnout, and the four pillars that do: meaning, autonomy, recovery, and connection." },
      { title: "Confidence & Self-Worth", body: "Confidence is a skill, not a personality trait. Concrete drills to rebuild it from the inside out." },
      { title: "Motivation", body: "The science of intrinsic motivation and how to design environments that pull you forward instead of pushing." },
      { title: "Emotional Resilience", body: "Naming, regulating, and using emotions as data instead of noise." },
      { title: "Sleep & The Mind", body: "Sleep is mental-health infrastructure. Practical hygiene that actually moves the needle." },
    ],
    relatedLinks: [
      { to: "/wellness/sleep", label: "Sleep", hint: "Recovery starts here" },
      { to: "/wellness/mens", label: "Men's Wellness", hint: "Stress, hormones, identity" },
      { to: "/wellness/womens", label: "Women's Wellness", hint: "Cycle, hormones, mind" },
      { to: "/ask-donny", label: "Ask Donny", hint: "Your AI co-pilot" },
      { to: "/dosha", label: "Take the Dosha Test", hint: "Your constitution in 6 minutes" },
      { to: "/marketplace?topic=mental-health", label: "Find a Therapist", hint: "Vetted practitioners" },
    ],
  },

  "wellness/nutrition": {
    eyebrow: "— Wellness · Nutrition",
    title: "Nutrition",
    subtitle:
      "Food as medicine, daily fuel, and a doorway into self-knowledge. Dosha-aware guidance, real-world routines, and recovery-first eating.",
    image:
      "https://images.unsplash.com/photo-1490645935967-10de6ba17061?auto=format&fit=crop&w=1600&q=80",
    marketplaceTopic: "nutrition",
    marketplaceTitle: "Find a nutritionist",
    askDonnyPrompt: "Build me a simple weekly eating plan that supports steady energy and good sleep.",
    pillars: [
      { title: "Food & Energy", body: "How macros, micros, and meal timing shape the energy curve of your day." },
      { title: "Dosha-Based Eating", body: "Why what works for vata may inflame pitta — eating in alignment with your constitution." },
      { title: "Healthy Routines", body: "The plate, the rhythm, the ritual: small structural shifts that outlast any diet." },
      { title: "Recovery Nutrition", body: "Eating to repair — for training, for stress, for the brain." },
      { title: "Lifestyle Eating", body: "Pleasure, culture, and family meals as foundations of long-term wellbeing." },
      { title: "Hydration & Digestion", body: "The basics, done well. Why digestive fire (agni) precedes any nutrition strategy." },
    ],
    relatedLinks: [
      { to: "/wellness/sports", label: "Sports Performance", hint: "Fuel + recovery" },
      { to: "/wellness/sleep", label: "Sleep", hint: "Food affects sleep affects food" },
      { to: "/dosha", label: "Dosha Test", hint: "Personalized eating starts here" },
      { to: "/marketplace?topic=nutrition", label: "Find a Nutritionist", hint: "Certified pros" },
      { to: "/ask-donny", label: "Ask Donny", hint: "Personalized prompts" },
      { to: "/shop", label: "Shop", hint: "Programs & guides" },
    ],
  },

  "wellness/sleep": {
    eyebrow: "— Wellness · Sleep",
    title: "Sleep",
    subtitle:
      "The most underrated wellness lever. Restore deep sleep with circadian-aware routines, light hygiene, and a calmer nervous system.",
    image:
      "https://images.unsplash.com/photo-1455642305367-68834a9d4337?auto=format&fit=crop&w=1600&q=80",
    marketplaceTopic: "sleep",
    marketplaceTitle: "Find a sleep specialist",
    askDonnyPrompt: "I struggle to fall asleep. Build me an evening wind-down routine.",
    pillars: [
      { title: "Sleep Architecture", body: "REM, deep, light — what each does and why fragmentation matters more than total hours." },
      { title: "Circadian Hygiene", body: "Light, temperature, meals, movement — the four levers that anchor a strong circadian rhythm." },
      { title: "Wind-Down Rituals", body: "Evening practices that signal safety to your nervous system and ease the transition to sleep." },
      { title: "Stress, Anxiety & Sleep", body: "The bidirectional loop — and how to break it without medication when possible." },
      { title: "Sleep & Recovery", body: "Athletic recovery, hormone repair, immune function — all happen during sleep." },
      { title: "Naps, Caffeine, Alcohol", body: "Honest guidance on what helps, what hurts, and what to do mid-shift." },
    ],
    relatedLinks: [
      { to: "/wellness/mental", label: "Mental Wellness", hint: "Calm the mind, win the night" },
      { to: "/wellness/sports", label: "Sports Performance", hint: "Sleep is performance" },
      { to: "/dosha", label: "Dosha Test", hint: "Your sleep blueprint" },
      { to: "/ask-donny", label: "Ask Donny", hint: "Build your wind-down" },
      { to: "/marketplace?topic=sleep", label: "Find a Specialist", hint: "Vetted sleep pros" },
      { to: "/shop", label: "Shop", hint: "Sleep programs" },
    ],
  },

  "wellness/sports": {
    eyebrow: "— Wellness · Sports Performance",
    title: "Sports Performance",
    subtitle:
      "Training, recovery, competition, and mental performance — for serious amateurs and professionals alike. Includes responsible coverage of steroid awareness.",
    image:
      "https://images.unsplash.com/photo-1517836357463-d25dfeac3438?auto=format&fit=crop&w=1600&q=80",
    marketplaceTopic: "fitness",
    marketplaceTitle: "Find a coach or trainer",
    askDonnyPrompt: "I want to train hard without burning out. Help me structure a balanced week.",
    pillars: [
      { title: "Training", body: "Progressive overload, periodization, and listening to the body. Programs you'll actually finish." },
      { title: "Recovery", body: "Sleep, nutrition, mobility, and active recovery as the engine of long-term performance." },
      { title: "Nutrition for Athletes", body: "Fueling for power, endurance, and brain — without obsession." },
      { title: "Mental Performance", body: "Focus, pre-competition routines, and the psychology of consistency under pressure." },
      { title: "Injury Prevention", body: "The boring habits that keep you healthy: mobility, eccentric work, and intelligent loading." },
      { title: "Steroid Awareness", body: "Honest, harm-reductive education about PEDs — risks, signals, and the conversation we don't have enough." },
    ],
    relatedLinks: [
      { to: "/wellness/mens", label: "Men's Wellness", hint: "Hormones, identity, drive" },
      { to: "/wellness/nutrition", label: "Nutrition", hint: "Fuel for performance" },
      { to: "/wellness/sleep", label: "Sleep", hint: "The undertraining trap" },
      { to: "/wellness/mental", label: "Mental Wellness", hint: "Performance is mental" },
      { to: "/marketplace?topic=fitness", label: "Find a Coach", hint: "Vetted pros" },
      { to: "/ask-donny", label: "Ask Donny", hint: "Talk through your goals" },
    ],
  },

  // ============================================================
  // RELATIONSHIPS
  // ============================================================
  "relationships/luv-connect": {
    eyebrow: "— Relationships · Luv Connect",
    title: "Luv Connect",
    subtitle:
      "A premium relationship experience inside the DONiTSH!T ecosystem — designed for people who want connection that's intentional, healthy, and growth-oriented.",
    image:
      "https://images.unsplash.com/photo-1518621736915-f3b1c41bfd00?auto=format&fit=crop&w=1600&q=80",
    marketplaceTopic: "relationships",
    marketplaceTitle: "Find a relationship coach",
    askDonnyPrompt: "What does a healthy modern relationship look like in practice?",
    pillars: [
      { title: "Intentional Connection", body: "Beyond apps and noise — connection rooted in values, vision, and self-awareness." },
      { title: "Emotional Maturity", body: "Why doing your own work first is the single best predictor of relationship health." },
      { title: "Compatibility Beyond Chemistry", body: "Lifestyle, values, communication style, and how to read them honestly." },
      { title: "Healthy Boundaries", body: "Defining yours, respecting theirs — the conversation that changes everything." },
      { title: "Courageous Conversations", body: "Naming what matters early. Difficult talks done with care." },
      { title: "Long-Term Vision", body: "Designing a relationship that supports both partners' growth and wellbeing." },
    ],
    relatedLinks: [
      { to: "/relationships/couples-connect", label: "Couples Connect", hint: "For those already partnered" },
      { to: "/relationships/communication", label: "Communication", hint: "The foundational skill" },
      { to: "/relationships/healthy", label: "Healthy Relationships", hint: "What good looks like" },
      { to: "/relationships/dating", label: "Dating", hint: "Modern dating, intentional" },
      { to: "/marketplace?topic=relationships", label: "Find a Coach", hint: "Vetted relationship pros" },
      { to: "/ask-donny", label: "Ask Donny", hint: "Your relationship co-pilot" },
    ],
  },

  "relationships/couples-connect": {
    eyebrow: "— Relationships · Couples Connect",
    title: "Couples Connect",
    subtitle:
      "Tools, conversations, and rituals for couples who want to keep growing together — through every season, from new love to long-term partnership.",
    image:
      "https://images.unsplash.com/photo-1519671482749-fd09be7ccebf?auto=format&fit=crop&w=1600&q=80",
    marketplaceTopic: "couples",
    marketplaceTitle: "Find a couples therapist",
    askDonnyPrompt: "Help us set up a weekly couples check-in that actually feels good.",
    pillars: [
      { title: "Weekly Check-Ins", body: "A simple ritual that catches small issues before they become big ones." },
      { title: "Conflict Repair", body: "The science of relationship rupture — and the conversational tools that mend it." },
      { title: "Intimacy (Emotional + Physical)", body: "How desire works, why it ebbs, and the conditions in which it flourishes." },
      { title: "Money, Roles, & Logistics", body: "The unsexy conversations that protect the sexy ones." },
      { title: "Growing Together", body: "Designing shared rituals, goals, and adventures that bond instead of drift." },
      { title: "When to Seek Help", body: "Honest signals that it's time to invite a third voice — and how to choose the right one." },
    ],
    relatedLinks: [
      { to: "/relationships/luv-connect", label: "Luv Connect", hint: "For singles & early dating" },
      { to: "/relationships/communication", label: "Communication", hint: "The skill couples need most" },
      { to: "/relationships/healthy", label: "Healthy Relationships", hint: "What good looks like" },
      { to: "/families/parenting", label: "Parenting", hint: "Co-parenting strong" },
      { to: "/marketplace?topic=couples", label: "Find a Couples Therapist", hint: "Vetted pros" },
      { to: "/ask-donny", label: "Ask Donny", hint: "Couples prompts" },
    ],
  },

  "relationships/healthy": {
    eyebrow: "— Relationships · Healthy Relationships",
    title: "Healthy Relationships",
    subtitle:
      "What healthy actually looks like — and the small daily patterns that distinguish secure, growth-oriented relationships from drama-driven ones.",
    image:
      "https://images.unsplash.com/photo-1469571486292-0ba58a3f068b?auto=format&fit=crop&w=1600&q=80",
    marketplaceTopic: "relationships",
    marketplaceTitle: "Find a relationship coach",
    askDonnyPrompt: "What are the early signs of a truly healthy relationship?",
    pillars: [
      { title: "Secure Attachment", body: "How to recognize and build secure-functioning relationships — even if you weren't raised in one." },
      { title: "Mutual Respect & Equity", body: "Power, voice, and decision-making in healthy partnerships." },
      { title: "Trust & Transparency", body: "The slow work of building (and rebuilding) the most important currency." },
      { title: "Healthy Conflict", body: "Disagreements are inevitable; contempt isn't. The difference matters." },
      { title: "Independence Within Connection", body: "Why strong individuals make stronger couples." },
      { title: "Repair Skills", body: "The single highest-leverage skill in long-term partnerships." },
    ],
    relatedLinks: [
      { to: "/relationships/communication", label: "Communication", hint: "Practice these skills" },
      { to: "/relationships/luv-connect", label: "Luv Connect", hint: "For singles" },
      { to: "/relationships/couples-connect", label: "Couples Connect", hint: "For partners" },
      { to: "/wellness/mental", label: "Mental Wellness", hint: "Your inner work matters" },
      { to: "/marketplace?topic=relationships", label: "Find a Therapist", hint: "Vetted pros" },
      { to: "/ask-donny", label: "Ask Donny", hint: "Reflective prompts" },
    ],
  },

  "relationships/communication": {
    eyebrow: "— Relationships · Communication",
    title: "Communication",
    subtitle:
      "The skill that quietly underwrites every healthy connection. From asking better questions to delivering hard truths with care.",
    image:
      "https://images.unsplash.com/photo-1573497019418-b400bb3ab074?auto=format&fit=crop&w=1600&q=80",
    marketplaceTopic: "relationships",
    marketplaceTitle: "Find a communication coach",
    askDonnyPrompt: "How do I bring up something difficult without making it worse?",
    pillars: [
      { title: "Listening to Understand", body: "Most arguments are listening failures. How to listen so the other person feels heard." },
      { title: "Difficult Conversations", body: "Naming what matters, with care and without manipulation." },
      { title: "Non-Violent Communication", body: "Observation, feeling, need, request — a framework that disarms and unites." },
      { title: "Boundaries & Saying No", body: "The most loving 'no' is the one that protects the relationship from resentment." },
      { title: "Digital Communication", body: "Why text amplifies misunderstanding — and when to switch channels." },
      { title: "Repair After Rupture", body: "Apology isn't a single sentence. It's a sequence." },
    ],
    relatedLinks: [
      { to: "/relationships/healthy", label: "Healthy Relationships", hint: "Where communication lives" },
      { to: "/relationships/couples-connect", label: "Couples Connect", hint: "Apply with your partner" },
      { to: "/families/parenting", label: "Parenting", hint: "Communicate with kids" },
      { to: "/wellness/mental", label: "Mental Wellness", hint: "Your inner dialogue first" },
      { to: "/marketplace?topic=relationships", label: "Find a Pro", hint: "Vetted communication coaches" },
      { to: "/ask-donny", label: "Ask Donny", hint: "Practice difficult talks" },
    ],
  },

  "relationships/dating": {
    eyebrow: "— Relationships · Dating",
    title: "Dating",
    subtitle:
      "Modern dating, intentionally. Slow down the swipe, get clear on what matters, and learn to recognize compatibility beyond chemistry.",
    image:
      "https://images.unsplash.com/photo-1485178575877-1a13bf489dfe?auto=format&fit=crop&w=1600&q=80",
    marketplaceTopic: "relationships",
    marketplaceTitle: "Find a dating coach",
    askDonnyPrompt: "Help me write a dating profile that reflects who I really am.",
    pillars: [
      { title: "Knowing What You Want", body: "Before you swipe — clarity on values, lifestyle, and the season you're in." },
      { title: "Healthy Early Dating", body: "Pacing, openness, and noticing red and green flags without paranoia." },
      { title: "Online Dating, Done Better", body: "Tactics for apps + offline that respect your time and energy." },
      { title: "Attachment in Dating", body: "How your attachment style shapes attraction — and what to do about it." },
      { title: "Sex & Intimacy Conversations", body: "Consent, desire, and the talks that build trust early." },
      { title: "When to Walk Away", body: "Sunk cost is real. So is dignity." },
    ],
    relatedLinks: [
      { to: "/relationships/luv-connect", label: "Luv Connect", hint: "The premium dating experience" },
      { to: "/relationships/healthy", label: "Healthy Relationships", hint: "What you're aiming for" },
      { to: "/relationships/communication", label: "Communication", hint: "The skill stack" },
      { to: "/wellness/mental", label: "Mental Wellness", hint: "Inner work first" },
      { to: "/marketplace?topic=relationships", label: "Find a Coach", hint: "Vetted dating pros" },
      { to: "/ask-donny", label: "Ask Donny", hint: "Dating prompts & reflections" },
    ],
  },

  "relationships/wellness": {
    eyebrow: "— Relationships · Relationship Wellness",
    title: "Relationship Wellness",
    subtitle:
      "Connection as a wellness pillar. The science (and art) of nurturing the relationships that nurture you — romantic, family, friendship, and self.",
    image:
      "https://images.unsplash.com/photo-1529390079861-591de354faf5?auto=format&fit=crop&w=1600&q=80",
    marketplaceTopic: "relationships",
    marketplaceTitle: "Find a relationship pro",
    askDonnyPrompt: "How do I keep my closest relationships healthy when life gets busy?",
    pillars: [
      { title: "Connection as Health", body: "The Harvard study and others: relationships are the strongest predictor of long-term wellbeing." },
      { title: "Friendship Health", body: "Why adult friendships need maintenance — and how to do it without it feeling forced." },
      { title: "Family Dynamics", body: "Patterns we inherit, patterns we choose. Healing without breaking." },
      { title: "Self-Relationship", body: "The most consequential relationship you'll ever have." },
      { title: "Loneliness", body: "A modern epidemic — and the practical antidotes." },
      { title: "Boundaries Across All Relationships", body: "What's yours, what's theirs, what's shared." },
    ],
    relatedLinks: [
      { to: "/relationships/healthy", label: "Healthy Relationships", hint: "Romantic focus" },
      { to: "/relationships/couples-connect", label: "Couples Connect", hint: "For partners" },
      { to: "/wellness/mental", label: "Mental Wellness", hint: "Inner foundations" },
      { to: "/families/wellness", label: "Family Wellness", hint: "Family ecosystem" },
      { to: "/marketplace?topic=relationships", label: "Find a Pro", hint: "Vetted practitioners" },
      { to: "/ask-donny", label: "Ask Donny", hint: "Reflect on your connections" },
    ],
  },

  // ============================================================
  // FAMILIES & PARENTING
  // ============================================================
  "families/parenting": {
    eyebrow: "— Families & Parenting · Parenting",
    title: "Parenting",
    subtitle:
      "Parenting skills, presence, and patience — informed by attachment research, neuroscience, and the lived experience of being human.",
    image:
      "https://images.unsplash.com/photo-1511895426328-dc8714191300?auto=format&fit=crop&w=1600&q=80",
    marketplaceTopic: "family-support",
    marketplaceTitle: "Find a parenting coach",
    askDonnyPrompt: "Help me become a calmer, more present parent.",
    pillars: [
      { title: "Attachment & Connection", body: "The single most studied predictor of child wellbeing — and how to build it daily." },
      { title: "Discipline Without Shame", body: "Setting limits with warmth. Why connection-first is the future." },
      { title: "Co-Parenting", body: "Building a parenting team — together, separated, or blended." },
      { title: "Single Parenting", body: "Resources, rhythm, and self-care for the parent doing it alone." },
      { title: "Screen Time & Social Media", body: "Practical, age-appropriate guidance without panic." },
      { title: "Parent Self-Care", body: "You can't pour from an empty cup. The non-negotiables." },
    ],
    relatedLinks: [
      { to: "/families/wellness", label: "Family Wellness", hint: "Family rhythm" },
      { to: "/families/teens", label: "Teens & Youth", hint: "When they grow up" },
      { to: "/relationships/couples-connect", label: "Couples Connect", hint: "Parenting partnership" },
      { to: "/wellness/mental", label: "Mental Wellness", hint: "Calm parents, calm kids" },
      { to: "/marketplace?topic=family-support", label: "Find a Pro", hint: "Vetted parenting coaches" },
      { to: "/ask-donny", label: "Ask Donny", hint: "Parenting prompts" },
    ],
  },

  "families/wellness": {
    eyebrow: "— Families & Parenting · Family Wellness",
    title: "Family Wellness",
    subtitle:
      "Rituals, routines, and rhythms that make family life a wellness practice — not just a logistics problem.",
    image:
      "https://images.unsplash.com/photo-1609220136736-443140cffec6?auto=format&fit=crop&w=1600&q=80",
    marketplaceTopic: "family-support",
    marketplaceTitle: "Find a family practitioner",
    askDonnyPrompt: "Design a simple weekly rhythm for our family.",
    pillars: [
      { title: "Family Rituals", body: "Why small rituals (meals, bedtimes, weekend rhythms) matter more than grand vacations." },
      { title: "Mealtimes Together", body: "The single highest-leverage daily habit for family wellbeing." },
      { title: "Movement as a Family", body: "Active families build healthier kids — and stronger bonds." },
      { title: "Family Mental Health", body: "Emotional climate, repair after conflict, modeling regulation." },
      { title: "Tech-Healthy Households", body: "Building a relationship with screens that serves the family." },
      { title: "Multi-Generational Care", body: "Caring for kids and aging parents — without losing yourself." },
    ],
    relatedLinks: [
      { to: "/families/parenting", label: "Parenting", hint: "Parent skills" },
      { to: "/families/teens", label: "Teens & Youth", hint: "Older kids" },
      { to: "/wellness/nutrition", label: "Nutrition", hint: "Family eating" },
      { to: "/wellness/sleep", label: "Sleep", hint: "Family sleep" },
      { to: "/marketplace?topic=family-support", label: "Find a Pro", hint: "Family therapists" },
      { to: "/ask-donny", label: "Ask Donny", hint: "Family prompts" },
    ],
  },

  // ============================================================
  // BUSINESS & CORPORATE
  // ============================================================
  "business/partners": {
    eyebrow: "— Business & Corporate · Partner Businesses",
    title: "Partner Businesses",
    subtitle:
      "Vetted businesses in the DONiTSH!T ecosystem — therapists, coaches, studios, retreats, and wellness brands offering meaningful value to our community.",
    image:
      "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=1600&q=80",
    marketplaceTopic: undefined,
    marketplaceTitle: "Featured partner practitioners",
    askDonnyPrompt: "What should I look for in a wellness business partner?",
    pillars: [
      { title: "Vetted Practitioners", body: "Every business in our directory is reviewed for credentials, ethics, and quality of care." },
      { title: "Special Offers", body: "Members enjoy exclusive offers from partner businesses." },
      { title: "Categories", body: "Therapists, coaches, nutritionists, studios, retreats, corporate wellness, and more." },
      { title: "Visibility for Pros", body: "Featured and premium listings drive visibility across the entire ecosystem." },
      { title: "List Your Business", body: "A free standard listing for any wellness pro. Upgrade for featured/premium placement." },
      { title: "Direct Bookings", body: "Direct booking and consult requests are launching inside the marketplace." },
    ],
    relatedLinks: [
      { to: "/marketplace", label: "Browse Marketplace", hint: "All listings" },
      { to: "/business/directory", label: "Business Directory", hint: "Browse by category" },
      { to: "/business/offers", label: "Special Offers", hint: "Member discounts" },
      { to: "/business/corporate", label: "Corporate Wellness", hint: "For teams" },
      { to: "/marketplace/register", label: "List Your Business", hint: "Free listing" },
      { to: "/ask-donny", label: "Ask Donny", hint: "Find the right pro" },
    ],
  },

  "business/corporate": {
    eyebrow: "— Business & Corporate · Corporate Wellness",
    title: "Corporate Wellness",
    subtitle:
      "Wellness programs that actually move the needle for teams — built on what we know works: connection, autonomy, recovery, and meaning.",
    image:
      "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?auto=format&fit=crop&w=1600&q=80",
    marketplaceTopic: "corporate",
    marketplaceTitle: "Find a corporate wellness partner",
    askDonnyPrompt: "Help me design a wellness program for a 50-person company.",
    pillars: [
      { title: "Burnout Prevention", body: "Beyond meditation apps — designing work that's sustainable at the organizational level." },
      { title: "Leadership Wellness", body: "Wellness for the people who set the culture. They go first." },
      { title: "Team Workshops", body: "Onsite and virtual workshops on stress, communication, and resilience." },
      { title: "1:1 Coaching", body: "Premium coaching for execs, founders, and high-performers." },
      { title: "Wellness Audits", body: "Honest assessments of your org's wellness posture — and a path forward." },
      { title: "Speaking & Keynotes", body: "Off-sites, all-hands, and conferences." },
    ],
    relatedLinks: [
      { to: "/business/partners", label: "Partner Businesses", hint: "Corporate-ready partners" },
      { to: "/business/offers", label: "Special Offers", hint: "Team discounts" },
      { to: "/marketplace?category=corporate-wellness", label: "Marketplace", hint: "Corporate listings" },
      { to: "/wellness/mental", label: "Mental Wellness", hint: "The foundation" },
      { to: "/support", label: "Contact Us", hint: "Request a proposal" },
      { to: "/ask-donny", label: "Ask Donny", hint: "Strategy prompts" },
    ],
  },

  "business/offers": {
    eyebrow: "— Business & Corporate · Special Offers",
    title: "Special Offers",
    subtitle:
      "Member-exclusive offers from partner businesses across the DONiTSH!T ecosystem — therapy intros, retreat discounts, program bundles, and more.",
    image:
      "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?auto=format&fit=crop&w=1600&q=80",
    marketplaceTopic: undefined,
    marketplaceTitle: "Featured partners with offers",
    askDonnyPrompt: "Which offers are most aligned with my dosha and goals?",
    pillars: [
      { title: "How It Works", body: "Browse offers publicly. Sign in to reveal promo codes and member-only deals." },
      { title: "Vetted Partners Only", body: "Every offer is from a verified partner business — quality, not quantity." },
      { title: "Rotating Selection", body: "New offers added monthly. Bookmark this page." },
      { title: "Categories", body: "Therapy intros, coaching bundles, retreats, nutrition programs, fitness, corporate wellness." },
      { title: "Refer & Earn", body: "Share offers with friends and earn credits — rolling out across the ecosystem." },
      { title: "For Partners", body: "List your business and add member offers to boost visibility." },
    ],
    relatedLinks: [
      { to: "/business/partners", label: "Partner Businesses", hint: "Browse all partners" },
      { to: "/marketplace", label: "Marketplace", hint: "Full directory" },
      { to: "/marketplace/register", label: "List Your Business", hint: "Become a partner" },
      { to: "/business/corporate", label: "Corporate Wellness", hint: "Team offers" },
      { to: "/signup", label: "Sign Up", hint: "Reveal member codes" },
      { to: "/ask-donny", label: "Ask Donny", hint: "Personalized recommendations" },
    ],
  },

  "business/directory": {
    eyebrow: "— Business & Corporate · Business Directory",
    title: "Business Directory",
    subtitle:
      "Browse the full DONiTSH!T marketplace by category — therapists, coaches, studios, retreats, nutritionists, and more.",
    image:
      "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=1600&q=80",
    marketplaceTopic: undefined,
    marketplaceTitle: "Featured directory listings",
    askDonnyPrompt: "Help me find the right kind of practitioner for what I'm going through.",
    pillars: [
      { title: "Therapists & Counsellors", body: "Licensed mental-health professionals." },
      { title: "Coaches", body: "Life, performance, executive, relationship, and wellness coaches." },
      { title: "Nutritionists & Dietitians", body: "Food, supplementation, and dosha-aware eating." },
      { title: "Studios & Gyms", body: "Yoga, movement, strength, and recovery spaces." },
      { title: "Retreats", body: "Multi-day wellness immersions worldwide." },
      { title: "Corporate Wellness Partners", body: "For teams of 5 to 5,000." },
    ],
    relatedLinks: [
      { to: "/marketplace", label: "Open Marketplace", hint: "Full search & filters" },
      { to: "/business/partners", label: "Partner Businesses", hint: "About our partners" },
      { to: "/business/offers", label: "Special Offers", hint: "Member-only deals" },
      { to: "/business/corporate", label: "Corporate Wellness", hint: "For teams" },
      { to: "/marketplace/register", label: "List Your Business", hint: "Free listing" },
      { to: "/ask-donny", label: "Ask Donny", hint: "Get a recommendation" },
    ],
  },
};

export const ECOSYSTEM_TOPIC_ALIASES = {
  // Legacy or cross-IA aliases
  "wellness/mens": "mens-wellness-redirect",
  "wellness/womens": "womens-wellness-redirect",
  "families/teens": "teens-redirect",
};

// ---- Merge BASE_TOPICS with DEEP_CONTENT into the public export.
// Anything provided in deepContent (editorial, quotes, reads, products) is
// added on top of the base config without losing the original pillars or links.
export const ECOSYSTEM_TOPICS = Object.fromEntries(
  Object.entries(BASE_TOPICS).map(([key, base]) => [
    key,
    { ...base, ...(DEEP_CONTENT[key] || {}) },
  ])
);
